import '../lib/vendor/bala.DualSelectList/js/bala.DualSelectList';
import {initLucideIcons} from './lucide-icons';

type OrderColumn = {
    id: string;
    name: string;
};

type OrderingConfig = {
    availableColumns: OrderColumn[];
    selectedColumns: OrderColumn[];
    multiOrdering: Record<string, string>;
    saveUrl: string;
    labels: {
        moveAll: string;
        columnsToOrder: string;
        orderingColumns: string;
        asc: string;
        desc: string;
    };
};

type DualSelectListApi = {
    getSelection(): OrderColumn[];
    getCandidates(): OrderColumn[];
    setCandidate(items?: OrderColumn[]): void;
    setSelection(items?: OrderColumn[]): void;
};

declare global {
    interface Window {
        initDatagridOrdering?: (root?: ParentNode | Document) => void;
    }

    interface JQuery {
        DualSelectList(options: Record<string, unknown>): DualSelectListApi;
    }
}

const dslColors = {
    itemBackground: '#eff6ff',
    itemHoverBackground: '#2563eb',
    itemText: '#111827',
    itemPlaceholderBorder: '#d1d5db',
};

function readConfig(root: HTMLElement): OrderingConfig | null {
    const raw = root.dataset.orderingConfig;

    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw) as OrderingConfig;
    } catch {
        return null;
    }
}

function getCurrentDirections(root: HTMLElement): Record<string, string> {
    const values: Record<string, string> = {};

    root.querySelectorAll<HTMLSelectElement>('.nais-datagrid-ordering__directions select').forEach((select) => {
        if (select.id) {
            values[select.id] = select.value;
        }
    });

    return values;
}

function rebuildDirectionSelects(
    root: HTMLElement,
    selected: OrderColumn[],
    multiOrdering: Record<string, string>,
    labels: OrderingConfig['labels'],
): void {
    const container = root.querySelector('.nais-datagrid-ordering__directions');

    if (!container) {
        return;
    }

    const currentDirections = getCurrentDirections(root);

    container.innerHTML = '';

    selected.forEach((column, index) => {
        const direction = currentDirections[column.id] ?? multiOrdering[column.id] ?? 'asc';
        const field = document.createElement('div');
        field.className = 'nais-datagrid-ordering__direction-field';
        field.id = `grid-col-${column.id}`;

        const label = document.createElement('label');
        label.className = 'nais-datagrid-ordering__direction-label';
        label.htmlFor = `order-dir-${column.id}`;
        label.textContent = `${index + 1}. ${column.name}`;

        const select = document.createElement('select');
        select.id = column.id;
        select.className = 'form-control nais-datagrid-ordering__direction-select select2-ignore';

        const ascOption = document.createElement('option');
        ascOption.value = 'asc';
        ascOption.textContent = labels.asc;
        ascOption.selected = direction === 'asc';

        const descOption = document.createElement('option');
        descOption.value = 'desc';
        descOption.textContent = labels.desc;
        descOption.selected = direction === 'desc';

        select.append(ascOption, descOption);
        field.append(label, select);
        container.append(field);
    });
}

const observerKey = 'naisOrderingObserver';
const dslKey = 'naisOrderingDsl';

function wireMoveAll(
    selectContainer: HTMLElement,
    dsl: DualSelectListApi,
    syncDirections: () => void,
): void {
    const $ = window.jQuery;
    $(selectContainer).off('click.datagridOrderingMoveAll').on('click.datagridOrderingMoveAll', '.dsl-move-all', () => {
        if (dsl.getCandidates().length > 0) {
            dsl.setSelection(dsl.getCandidates());
            dsl.setCandidate();
        } else {
            dsl.setCandidate(dsl.getSelection());
            dsl.setSelection();
        }
        syncDirections();
    });
}

function initOrderingPanel(root: HTMLElement): void {
    const config = readConfig(root);

    if (!config) {
        return;
    }

    const previousObserver = (root as HTMLElement & { [observerKey]?: MutationObserver })[observerKey];
    previousObserver?.disconnect();

    const selectContainer = root.querySelector<HTMLElement>('.nais-datagrid-ordering__dual-select');

    if (!selectContainer) {
        return;
    }

    const $ = window.jQuery;
    const $target = $(selectContainer);
    let dsl = (root as HTMLElement & { [dslKey]?: DualSelectListApi })[dslKey];

    if (!dsl) {
        $target.empty();

        dsl = $.fn.DualSelectList.call($target, {
            candidateItems: config.availableColumns,
            selectionItems: config.selectedColumns,
            moveAll: config.labels.moveAll,
            leftTitle: config.labels.columnsToOrder,
            rightTitle: config.labels.orderingColumns,
            colors: dslColors,
        }) as DualSelectListApi;

        (root as HTMLElement & { [dslKey]?: DualSelectListApi })[dslKey] = dsl;
        initLucideIcons(selectContainer);
    } else {
        dsl.setCandidate();
        dsl.setCandidate(config.availableColumns);
        dsl.setSelection();
        dsl.setSelection(config.selectedColumns);
    }

    const syncDirections = () => {
        rebuildDirectionSelects(root, dsl!.getSelection(), config.multiOrdering, config.labels);
    };

    syncDirections();
    wireMoveAll(selectContainer, dsl, syncDirections);

    const observer = new MutationObserver(() => {
        syncDirections();
    });

    observer.observe(selectContainer, {
        childList: true,
        subtree: true,
    });

    (root as HTMLElement & { [observerKey]?: MutationObserver })[observerKey] = observer;

    $(root).off('click.datagridOrdering').on('click.datagridOrdering', '[data-action="save-ordering"]', () => {
        const values: Record<string, string> = {};

        root.querySelectorAll<HTMLSelectElement>('.nais-datagrid-ordering__directions select').forEach((select) => {
            if (select.id) {
                values[select.id] = select.value;
            }
        });

        $.nette.ajax({
            type: 'POST',
            url: config.saveUrl,
            data: values,
        });
    });
}

export function initDatagridOrdering(root: ParentNode | Document = document): void {
    const panels = root === document
        ? document.querySelectorAll<HTMLElement>('[data-datagrid-ordering]')
        : (root as ParentNode).querySelectorAll<HTMLElement>('[data-datagrid-ordering]');

    panels.forEach((panel) => {
        initOrderingPanel(panel);
    });
}

window.initDatagridOrdering = initDatagridOrdering;
